import React, { useCallback } from 'react';
import { Modal, Button, Typography, Table, Badge } from '@crisellevaldez/speculo-ui';
import { RotateCw } from 'lucide-react';
import { Organization } from '../../../types/organization.types';
import {
  useIntegrationSetupRequests,
  useUpdateIntegrationSetupStatus,
  IntegrationSetupRequest,
} from '../../../hooks/useIntegrationSetupRequests';
import { ScrollableCell } from '../../common/ScrollableCell';

interface IntegrationSetupRequestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg: Organization | undefined;
  onSuccess?: () => void;
}

type Column = {
  key: string;
  header: string;
  isCentered?: boolean;
  width: string;
  cell: (request: IntegrationSetupRequest) => React.ReactNode;
};

export const IntegrationSetupRequestsModal: React.FC<IntegrationSetupRequestsModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const { data: requests = [], isLoading, refetch } = useIntegrationSetupRequests(selectedOrg?.id);
  const [updatingId, setUpdatingId] = React.useState<string | null>(null);

  const updateStatusMutation = useUpdateIntegrationSetupStatus();

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleStatusUpdate = (
    request: IntegrationSetupRequest,
    newStatus: 'pending' | 'processing' | 'complete'
  ) => {
    if (!selectedOrg?.id) return;

    setUpdatingId(request.id);
    updateStatusMutation.mutate(
      {
        requestId: request.id,
        newStatus,
        orgId: selectedOrg.id,
        flowId: request.flowId,
      },
      {
        onSuccess: () => {
          setUpdatingId(null);
          if (onSuccess) {
            onSuccess();
          }
        },
        onError: () => {
          setUpdatingId(null);
        },
      }
    );
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    // Handle Firestore Timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const columns: Column[] = [
    {
      key: 'flowName',
      header: 'Flow Name',
      width: '300px',
      cell: (request) => (
        <div className="truncate max-w-[300px] font-medium" title={request.flowName}>
          {request.flowName}
        </div>
      ),
    },
    {
      key: 'contactType',
      header: 'Contact Type',
      width: '50px',
      cell: (request) => <span className="capitalize">{request.contactType || '-'}</span>,
    },
    {
      key: 'crmSources',
      header: 'CRM Sources',
      width: '100px',
      cell: (request) =>
        request.crmSources?.length > 0 ? (
          <ScrollableCell>
            {request.crmSources.map((source, idx) => (
              <Badge key={idx} variant="info" className="dark:bg-blue-900/30 dark:text-blue-300">
                {source}
              </Badge>
            ))}
          </ScrollableCell>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">-</span>
        ),
    },
    {
      key: 'tags',
      header: 'Tags',
      width: '250px',
      cell: (request) =>
        request.useTags && request.tags?.length > 0 ? (
          <ScrollableCell>
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">
              ({request.tagsAction === 'include' ? 'Inc' : 'Exc'})
            </span>
            {request.tags.map((tag, idx) => (
              <Badge key={idx} variant="warning" className="dark:bg-amber-900/30 dark:text-amber-300">
                {tag}
              </Badge>
            ))}
          </ScrollableCell>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">-</span>
        ),
    },
    {
      key: 'submittedBy',
      header: 'Submitted By',
      width: '250px',
      cell: (request) => (
        <div className="truncate max-w-[250px]" title={request.submittedBy}>
          {request.submittedBy || '-'}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Date',
      width: '100px',
      cell: (request) => formatDate(request.createdAt),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '200px',
      cell: (request) => (
        <div className="flex items-center gap-2">
          {request.status !== 'complete' && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => handleStatusUpdate(request, 'complete')}
              isLoading={updatingId === request.id}
              disabled={updatingId === request.id}
            >
              Mark Complete
            </Button>
          )}
          {request.status === 'complete' && (
            <span className="text-xs text-green-600 dark:text-green-400">Completed</span>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <Modal.Header>
        <Typography variant="h3">Integration Setup Requests</Typography>
      </Modal.Header>

      <Modal.Body className="py-8">
        <div className="h-[300px] 3xl:h-[500px]">
          <Table<IntegrationSetupRequest>
            data={requests}
            columns={columns}
            keyExtractor={(item) => item.id}
            emptyStateText="No integration setup requests found"
            loading={isLoading}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline"
          size="md"
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          <RotateCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
